/**
 * The icon sets (spec docs/superpowers/specs/2026-10-29-branding-kit-and-brand-theme-design.md, section 3.6a).
 *
 * Every icon in the app is Lucide's, so a set is a style over Lucide's own SVGs (app/icon-sets.css),
 * never a second icon library and never a change to the screens that draw them. It is switched on by
 * `data-icon-set` on <html>, only while a page under the app sidebar is open, the same reach as the
 * app theme: the first-paint script (app-themes/app-theme-boot.ts) paints it while the page is being
 * parsed, and the theme scope (components/app-theme/app-theme-scope.tsx) paints it for a client-side
 * arrival and takes it off when the last app page goes. The play area, the portal and the admin
 * console never show a person's set; the portal draws the one its workspace chose, on its own shell, and on <html> while it is
 * mounted (lib/user-portal/portal-brand.ts), for what renders at the end of <body>.
 *
 * The choice lives on the user's account (users.preferences.iconSet), and a company's brand theme can stand in front of
 * it: a lock's set, else the person's own, else a suggested set, else Classic (paintedIconSet). Only a person's click
 * saves; a lock or a suggestion only paints, so an unlock brings their own back. The cookie mirrors what is PAINTED for
 * the first paint, in the app theme's format, `<set>.<userId>`, so a sign-in by someone else on the same browser can
 * drop it (reconcileIconSetOwner, called by the auth store's login()): the first-paint script is static and cannot know
 * who is signed in. On arrival it stands in for the person's own until the account answers. A cookie that says what this
 * tab paints is this tab's own mirror, never news from another. Nothing outside lib/theme reads the cookie (an isolation
 * spec holds that).
 *
 * The account and the server race on every load, as they do for the theme, and the rule is that store's
 * (app-themes/app-theme-store.ts): until the server's first answer an account answer records `saved` and nothing else, and
 * the set the page arrived with is `held`. Someone with no workspace to read never gets that answer (`readComing` false).
 *
 * Classic is the app's own look: the page with nothing added, so it writes no attribute and no cookie at all.
 *
 * The API keeps its own copy of the keys (apps/api/src/modules/users/icon-set.ts) and a parity spec
 * compares the two.
 */
import { isAppThemeOffPath } from './app-themes/app-theme-dom'

export const ICON_SETS = ['classic', 'light', 'bold', 'sharp', 'tiles'] as const
export type IconSet = (typeof ICON_SETS)[number]

/** The app's own icons, and what someone who never chose sees. */
export const DEFAULT_ICON_SET: IconSet = 'classic'

/**
 * The sets the stylesheet draws: every set but Classic, and the only values `applyIconSet` writes. A sample under <html> can
 * carry `classic` too, which has no rule and resets the set around it (app/icon-sets.css, "the nearest set wins").
 */
export const PAINTED_ICON_SETS: readonly IconSet[] = ICON_SETS.filter((set) => set !== 'classic')

/** The switch app/icon-sets.css is keyed on. */
export const ICON_SET_ATTRIBUTE = 'data-icon-set'

/** Mirrors the user's saved set for the first paint: `<set>.<userId>`. */
export const ICON_SET_COOKIE = 'hf-icon-set'

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365
const OWNER = /^[A-Za-z0-9-]{1,64}$/

export const isIconSet = (value: unknown): value is IconSet =>
  typeof value === 'string' && (ICON_SETS as readonly string[]).includes(value)

export interface IconSetCookie {
  set: IconSet
  /** The user the set belongs to; null when the cookie names nobody. */
  owner: string | null
}

/** The cookie's text as a set, or null when it is anything but `<catalogue set>[.<plain id>]`. */
export function parseIconSetCookieValue(value: string | null | undefined): IconSetCookie | null {
  if (typeof value !== 'string' || value === '') return null
  const parts = value.split('.')
  if (parts.length > 2) return null
  const [set, owner] = parts
  if (!isIconSet(set)) return null
  if (owner === undefined) return { set, owner: null }
  return OWNER.test(owner) ? { set, owner } : null
}

/** The exact `document.cookie` assignment that saves a set. Throws on a set outside the catalogue or a strange owner. */
export function iconSetCookieString(set: IconSet, userId: string): string {
  if (!isIconSet(set)) throw new Error(`Not an icon set: ${String(set)}`)
  if (!OWNER.test(userId)) throw new Error('The cookie owner must be a plain id')
  return `${ICON_SET_COOKIE}=${set}.${userId}; Path=/; SameSite=Lax; Max-Age=${ONE_YEAR_SECONDS}`
}

/** The cookie's raw text, or null. Reads `document.cookie` unless given a cookie header. */
export function readIconSetCookieValue(cookieString?: string): string | null {
  const source = cookieString ?? (typeof document === 'undefined' ? '' : document.cookie)
  const prefix = `${ICON_SET_COOKIE}=`
  for (const part of source.split(';')) {
    const pair = part.trim()
    if (pair.startsWith(prefix)) return pair.slice(prefix.length)
  }
  return null
}

/** The mirrored set, or null when there is none (never chosen) or it is not a readable one. */
export function readIconSetCookie(cookieString?: string): IconSetCookie | null {
  return parseIconSetCookieValue(readIconSetCookieValue(cookieString))
}

/** What the page draws: a lock's set, else the person's own, else a suggested set, else Classic. */
export function paintedIconSet(saved: IconSet | null, company: { value: IconSet; locked: boolean } | null): IconSet {
  if (company?.locked) return company.value
  return saved ?? company?.value ?? DEFAULT_ICON_SET
}

/** Mirror what is painted. Classic, or no signed-in user to own it, leaves no cookie. */
function writeIconSetCookie(set: IconSet, owner: string | null | undefined): void {
  if (typeof document === 'undefined') return
  if (set === DEFAULT_ICON_SET || !owner || !OWNER.test(owner)) {
    if (readIconSetCookieValue() !== null) document.cookie = `${ICON_SET_COOKIE}=; Path=/; SameSite=Lax; Max-Age=0`
    return
  }
  document.cookie = iconSetCookieString(set, owner)
}

/** Put a set on an element. Classic, nothing, or a value outside the catalogue takes it off. */
export function applyIconSet(set: IconSet | null, el: HTMLElement = document.documentElement): void {
  if (set !== null && PAINTED_ICON_SETS.includes(set)) el.setAttribute(ICON_SET_ATTRIBUTE, set)
  else el.removeAttribute(ICON_SET_ATTRIBUTE)
}

export interface IconSetSnapshot {
  /** What the page draws. */
  set: IconSet
  /** A lock has taken the choice out of the person's hands. */
  locked: boolean
}

export const SERVER_ICON_SET_SNAPSHOT: IconSetSnapshot = Object.freeze({ set: DEFAULT_ICON_SET, locked: false })

let openAppPages = 0
/** The person's own set, as far as this tab knows; the cookie's word until the account answers. */
let saved: IconSet | null = null
/** What the server last said; a failed read leaves it where it was. Null is also an answer: see `heard`. */
let company: { value: IconSet; locked: boolean } | null = null
/** Has the server answered since this page opened? */
let heard = false
/** The set the page keeps until the server's first answer, or a person's click. Null: nothing is being kept. */
let held: { set: IconSet } | null = null
let snapshot: IconSetSnapshot = SERVER_ICON_SET_SNAPSHOT
const listeners = new Set<() => void>()
let savesInFlight = 0
let epoch = 0

const painted = () => (held ? held.set : paintedIconSet(saved, company))

function emit() {
  const next: IconSetSnapshot = { set: painted(), locked: company?.locked === true }
  if (next.set === snapshot.set && next.locked === snapshot.locked) return
  snapshot = next
  for (const listener of [...listeners]) listener()
}

function paint() {
  applyIconSet(openAppPages > 0 && !isAppThemeOffPath(window.location.pathname) ? painted() : null)
  emit()
}

export function subscribeIconSet(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const getIconSetSnapshot = (): IconSetSnapshot => snapshot
export const getServerIconSetSnapshot = (): IconSetSnapshot => SERVER_ICON_SET_SNAPSHOT

/** The person's own set: what a failed save goes back to, never what a lock paints. */
export const getSavedIconSet = (): IconSet | null => saved

/**
 * Another tab may have saved a set (no event crosses tabs): read the cookie again. What this tab paints is its own
 * mirror and says nothing; under a lock a choice from a tab that has not heard of it is kept, and the mirror goes back.
 */
export function refreshIconSetFromCookie(): void {
  const mirror = readIconSetCookie()
  const mirrored = mirror?.set ?? DEFAULT_ICON_SET
  if (mirrored === painted()) return
  saved = mirrored
  held = null
  if (painted() !== mirrored) writeIconSetCookie(painted(), mirror?.owner)
  paint()
}

const refreshWhenVisible = () => {
  if (document.visibilityState === 'visible') refreshIconSetFromCookie()
}

/**
 * An app page opened: paint the mirrored set, as the first-paint script does (a client-side arrival
 * has no script), and watch for another tab's choice. Returns the function that closes it; the last
 * one closed takes the set off <html>, because a client-side move to the portal or the console reloads
 * nothing.
 */
export function enterIconSetScope(): () => void {
  openAppPages += 1
  if (openAppPages === 1) {
    saved = readIconSetCookie()?.set ?? null
    window.addEventListener('focus', refreshIconSetFromCookie)
    document.addEventListener('visibilitychange', refreshWhenVisible)
  }
  paint()
  let open = true
  return () => {
    if (!open) return
    open = false
    openAppPages = Math.max(0, openAppPages - 1)
    if (openAppPages === 0) {
      company = null
      heard = false
      held = null
      window.removeEventListener('focus', refreshIconSetFromCookie)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
      paint()
    }
  }
}

/** The person chose `next`: keep it, mirror what is now painted, paint it. */
export function setIconSet(next: IconSet | null, owner: string | null | undefined): void {
  if (next !== null && !isIconSet(next)) return
  saved = next
  held = null
  writeIconSetCookie(painted(), owner)
  paint()
}

// Saves and the account's answer: the same epoch as the app theme's (app-themes/app-theme-store.ts). An answer asked
// before a save may carry the old set, and must not overwrite the new.

export function beginIconSetSave(): void {
  savesInFlight += 1
  epoch += 1
}

export function endIconSetSave(): void {
  savesInFlight = Math.max(0, savesInFlight - 1)
  epoch += 1
}

export const iconSetSaveEpoch = (): number => epoch

/**
 * The account says `answer` (null: never chose). Keep it, and paint what that makes the set, unless a save overtook it.
 * With `readComing` and no answer from the server yet, it is only kept: see the header.
 */
export function adoptAccountIconSet(answer: IconSet | null, owner: string | null | undefined, askedAt: number, readComing = false): boolean {
  if (savesInFlight > 0 || askedAt !== epoch) return false
  if (readComing && !heard) {
    held ??= { set: painted() }
    saved = answer
    return true
  }
  setIconSet(answer, owner)
  return true
}

/** The server says what the company's theme does to the set (a successful read: a failed one never gets here). */
export function adoptEffectiveIconSet(next: { value: string; locked: boolean } | null, owner: string | null | undefined): void {
  company = next !== null && isIconSet(next.value) ? { value: next.value, locked: next.locked } : null
  heard = true
  held = null
  writeIconSetCookie(painted(), owner)
  paint()
}

/** Tests only: forget everything this module holds. */
export function resetIconSetStoreForTests(): void {
  openAppPages = 0
  saved = null
  company = null
  heard = false
  held = null
  snapshot = SERVER_ICON_SET_SNAPSHOT
  listeners.clear()
  savesInFlight = 0
  epoch = 0
  window.removeEventListener('focus', refreshIconSetFromCookie)
  document.removeEventListener('visibilitychange', refreshWhenVisible)
}

/**
 * A sign-in: drop a mirrored set that is not this person's (a shared browser), and take it off the
 * page, so the next person's first page is not drawn in the previous person's set. Their own arrives
 * from their account.
 */
export function reconcileIconSetOwner(userId: string | null | undefined): void {
  if (typeof document === 'undefined') return
  const current = readIconSetCookie()
  if (current && userId && current.owner === userId) return
  if (readIconSetCookieValue() !== null) document.cookie = `${ICON_SET_COOKIE}=; Path=/; SameSite=Lax; Max-Age=0`
  saved = null
  applyIconSet(null)
}
